import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Gender } from "@application/common/enums/gender";
import { ShirtSize } from "@application/common/enums/shirt-size";
import { getInvitationDatesByInvitationIdAsync } from "@application/features/invitation/invitationThunk";
import { persistor } from "@application/store";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { alpha, Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { SteperHeader } from "@ui/components/Header/SteperHeader";
import { HDatePicker } from "@ui/components/inputs/HDatePicker";
import { HInput } from "@ui/components/inputs/HInput";
import { HSelect } from "@ui/components/inputs/HSelect";
import { HSwitchGroup } from "@ui/components/inputs/HSwitchGroup";
import { HTabInput } from "@ui/components/inputs/HTabInput";
import { colCenter } from "@ui/style/style";
import { Add, Minus } from "iconsax-react";
import { useWatch } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EIconButton } from "@ui/components/event/button/EIconButton";
import { getEventByIdAsync } from "@application/features/Event/EventThunk";
import { GetPersonalInfoByUserIdAsync } from "@application/features/personalInfo/personalInfoThunk";
import { GetExternalByUserIdAsync, } from "@application/features/external/externalThunk";
import { useLazySearchAddressLocationQuery, useLazyGetLocationQuery, } from "@application/features/pdok/models/pdok-api";
import { FlagDropdown } from "@ui/components/event/flag-dropdown/FlagDropdown";
import { PhoneInputMasking } from "@ui/components/PhoneMasking/PhoneInputMasking";
import { AdultsInfoForm } from "./AdultInfo";
import { validateDate, validateIsFutureDate } from "@ui/constants/Helper";
import { formatLocalizedDate } from "@infrastructure/helpers";
export const PersonalInfoForm = ({ control, errors, personId, formMod = "edit", eventType, setValue, setError, clearErrors, invitationId, userId, reset, }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const [isNLAddress, setIsNLAddress] = useState(true);
    const addition = useWatch({ control, name: "PersonalInfo.addition" });
    const postalCode = useWatch({ control, name: "PersonalInfo.postalcode" });
    const houseNumber = useWatch({ control, name: "PersonalInfo.houseNumber" });
    const childrenCount = useWatch({ control, name: "PersonalInfo.ChildrenCount" }) || 0;
    const adultsCount = useWatch({ control, name: "PersonalInfo.AdultsCount" }) || 0;
    const { personalInfo } = useAppSelector((state) => state.person);
    const { linkVerification } = useAppSelector((state) => state.event);
    const { invitationDates } = useAppSelector((state) => state.invitation);
    const { dietaryOptions } = useAppSelector((state) => state.dietaryOptions);
    const urlParams = new URLSearchParams(window.location.search);
    const externalId = urlParams.get("id");
    const attendanceOptions = useMemo(() => {
        if (!invitationDates)
            return [];
        return invitationDates.map((date) => ({
            label: formatLocalizedDate(date.invitedDate, i18n.language),
            value: date.id,
        }));
    }, [invitationDates, i18n.language]);
    const isRegistered = formMod === "edit" ? false : true;
    const [showAddition, setShowAddition] = useState(false);
    const [triggerAddressSearch] = useLazySearchAddressLocationQuery();
    const [triggerGetLocation] = useLazyGetLocationQuery();
    useEffect(() => {
        if (formMod &&
            formMod === "view" &&
            personalInfo &&
            personalInfo.address !== "") {
            return;
        }
        if (personalInfo &&
            personalInfo.language != "INT" &&
            (!postalCode?.trim() || !houseNumber?.trim())) {
            setValue("PersonalInfo.Address", "", { shouldValidate: false });
            setValue("PersonalInfo.addition", "", { shouldValidate: false });
            setShowAddition(false);
            return;
        }
        const searchAddress = async () => {
            if (postalCode && houseNumber && postalCode != "" && houseNumber != "") {
                const searchQuery = `${postalCode} ${houseNumber}${addition ? `-${addition}` : ""}`;
                // Format postal code for second search (normalized format)
                const normalizedPostalCode = postalCode
                    .replace(/\s+/g, "") // Remove all spaces
                    .match(/^(\d{4})([a-zA-Z]{2})$/i); // Match 4 digits and 2 letters
                const formattedPostalCode = normalizedPostalCode
                    ? `${normalizedPostalCode[1]}${normalizedPostalCode[2]}` // Combine without space
                    : postalCode.replace(/\s+/g, "").toUpperCase(); // Fallback to simple space removal
                const searchQuery1 = `${formattedPostalCode} ${houseNumber}${addition ? `-${addition}` : ""}`;
                try {
                    const searchResult = await triggerAddressSearch(searchQuery).unwrap();
                    const searchResult1 = await triggerAddressSearch(searchQuery1).unwrap();
                    if (searchResult.length === 0) {
                        setValue("PersonalInfo.Address", "", { shouldValidate: false });
                        setError("PersonalInfo.Address", {
                            type: "manual",
                            message: t("validations.addressNotFound"),
                        });
                        return;
                    }
                    clearErrors("PersonalInfo.addition");
                    clearErrors("PersonalInfo.Address");
                    setValue("PersonalInfo.Address", "", { shouldValidate: false });
                    if (searchResult.length === 1 || searchResult1.length == 1) {
                        if (searchResult.length == 1) {
                            const locationDetails = await triggerGetLocation(searchResult[0].id).unwrap();
                            setValue("PersonalInfo.Address", locationDetails.weergavenaam, {
                                shouldValidate: true,
                            });
                        }
                        if (searchResult1.length == 1) {
                            const locationDetails = await triggerGetLocation(searchResult[0].id).unwrap();
                            setValue("PersonalInfo.Address", locationDetails.weergavenaam, {
                                shouldValidate: true,
                            });
                        }
                        setShowAddition(false);
                    }
                    else {
                        const fullHouseNumber = addition
                            ? `${houseNumber}${addition}`
                            : houseNumber;
                        // Try exact match with toevoeging (if provided)
                        // const exactMatch = searchResult.find((item) => {
                        //   const regex = new RegExp(
                        //     `\\b${fullHouseNumber}\\b,\\s*${postalCode}\\b`,
                        //     "i"
                        //   );
                        //   return regex.test(item.weergavenaam);
                        // });
                        const exactMatch = searchResult.find((item) => {
                            // Format postal code variations for comparison
                            const postalCodeWithSpace = postalCode
                                .replace(/([0-9]+)([a-zA-Z]+)/, "$1 $2")
                                .toUpperCase();
                            const postalCodeNoSpace = postalCode
                                .replace(/\s+/g, "")
                                .toUpperCase();
                            // Create more flexible pattern for house number and postal code
                            const regex = new RegExp(`\\b${houseNumber}\\b[,\\s]*(?:${postalCodeWithSpace}|${postalCodeNoSpace})\\b`, "i");
                            return regex.test(item.weergavenaam);
                        });
                        if (!exactMatch) {
                            setShowAddition(true);
                            if (!addition) {
                                setError("PersonalInfo.addition", {
                                    type: "manual",
                                    message: t("validations.additionRequired"),
                                });
                                return; // Stop further execution if addition is required but missing
                            }
                        }
                        // Pick exactMatch if found, else fallback to best score
                        const selectedAddress = exactMatch
                            ? exactMatch
                            : searchResult.reduce((prev, current) => prev.score > current.score ? prev : current);
                        const locationDetails = await triggerGetLocation(selectedAddress.id).unwrap();
                        setValue("PersonalInfo.Address", locationDetails.weergavenaam, {
                            shouldValidate: true,
                        });
                        setShowAddition(false);
                    }
                }
                catch (error) {
                    setValue("PersonalInfo.Address", "", { shouldValidate: false });
                    setValue("PersonalInfo.addition", "", { shouldValidate: false });
                    setError("PersonalInfo.Address", {
                        type: "manual",
                        message: t("validations.addressLookupFailed"),
                    });
                }
            }
        };
        const timeoutId = setTimeout(searchAddress, 700);
        return () => clearTimeout(timeoutId);
    }, [
        postalCode,
        houseNumber,
        addition,
        setValue,
        setError,
        clearErrors,
        triggerAddressSearch,
        triggerGetLocation,
        t,
    ]);
    useEffect(() => {
        if (linkVerification?.data?.eventId) {
            dispatch(getEventByIdAsync(linkVerification?.data?.eventId));
        }
    }, [linkVerification?.data?.eventId]);
    useEffect(() => {
        if (!setValue)
            return;
        // Validity check only when formMod is "view"
        if (formMod === "view") {
            const isValid = personalInfo && personalInfo.id === personId;
            if (!isValid) {
                reset((prev) => ({
                    ...prev,
                    PersonalInfo: Object.fromEntries(Object.keys(prev.PersonalInfo || {}).map((key) => [key, ""])),
                }));
                return;
            }
        }
        if (personalInfo) {
            const language = personalInfo.language || "NL";
            setIsNLAddress(language === "NL");
            setValue("PersonalInfo.language", language, { shouldValidate: true });
            setValue("PersonalInfo.FirstName", personalInfo.firstName || "");
            setValue("PersonalInfo.MiddleName", personalInfo.middleName || "");
            setValue("PersonalInfo.LastName", personalInfo.lastName || "");
            setValue("PersonalInfo.email", personalInfo.email || "");
            setValue("PersonalInfo.Phone", personalInfo.phone || "");
            setValue("PersonalInfo.DateOfBirth", personalInfo.dateOfBirth || null);
            setValue("PersonalInfo.language", personalInfo.language || "NL");
            setValue("PersonalInfo.Address", personalInfo.address || "");
            setValue("PersonalInfo.postalcode", personalInfo.postalCode || "");
            setValue("PersonalInfo.houseNumber", personalInfo.houseNumber || "");
            setValue("PersonalInfo.addition", personalInfo.addition || "");
            setValue("PersonalInfo.Gender", personalInfo.gender || "");
            setValue("PersonalInfo.ShirtSize", personalInfo.shirtSize || "");
            setValue("PersonalInfo.Option", Number(personalInfo.option) || 3);
            setValue("PersonalInfo.AdultsCount", personalInfo.adultsCount || 0);
            setValue("PersonalInfo.ChildrenCount", personalInfo.childrenCount || 0);
            const companyLabel = linkVerification?.data?.user.externalCompanyName ||
                (linkVerification?.data?.user.companyName?.includes("Van Dijnsen Beveiligingstechniek")
                    ? "Van Dijnsen"
                    : linkVerification?.data?.user.companyName || "");
            setValue("PersonalInfo.CompanyName", companyLabel);
            // setValue(
            //   "PersonalInfo.CompanyName",
            //   linkVerification?.data?.user.externalCompanyName ||
            //     linkVerification?.data?.user.companyName.includes(
            //       "Van Dijnsen Beveiligingstechniek"
            //     )
            //     ? "Van Dijnsen"
            //     : linkVerification?.data?.user.companyName || ""
            // );
            setValue("PersonalInfo.WillYouAttendEvent", String(personalInfo.willYouAttendEvent) || undefined);
            const selectedDates = attendanceOptions
                .filter((option) => personalInfo.attendeeDates?.some((dateId) => {
                // Ensure both are strings for comparison
                const savedId = String(dateId).toLowerCase().trim();
                const optionId = String(option.value).toLowerCase().trim();
                return savedId === optionId;
            }))
                .map((option) => option.value);
            // Set with shouldValidate to ensure proper form state
            setValue("PersonalInfo.AttendanceDateIds", selectedDates, {
                shouldValidate: false,
                shouldDirty: true,
            });
            // const selectedDates = attendanceOptions
            //   .filter((option) =>
            //     personalInfo.attendeeDates?.some((dateId) => dateId === option.value)
            //   )
            //   .map((option) => option.value);
            // setValue("PersonalInfo.AttendanceDateIds", selectedDates);
        }
        else if (linkVerification?.data?.user) {
            const { user } = linkVerification.data;
            setValue("PersonalInfo.FirstName", user.firstName || "");
            setValue("PersonalInfo.MiddleName", user.middleName || "");
            setValue("PersonalInfo.LastName", user.lastName || "");
            setValue("PersonalInfo.Phone", user.mobileNumber || "");
            const companyLabel = linkVerification?.data?.user.externalCompanyName ||
                (linkVerification?.data?.user.companyName?.includes("Van Dijnsen Beveiligingstechniek")
                    ? "Van Dijnsen"
                    : linkVerification?.data?.user.companyName || "");
            setValue("PersonalInfo.CompanyName", companyLabel, {
                shouldValidate: false,
            });
            // setValue(
            //   "PersonalInfo.CompanyName",
            //   linkVerification?.data?.user.externalCompanyName ||
            //     linkVerification?.data?.user.companyName.includes(
            //       "Van Dijnsen Beveiligingstechniek"
            //     )
            //     ? "Van Dijnsen"
            //     : linkVerification?.data?.user.companyName || "",
            //   { shouldValidate: false }
            // );
        }
    }, [personalInfo, linkVerification, setValue, formMod, attendanceOptions]);
    const genderOptions = useMemo(() => Object.values(Gender).map((value) => ({
        label: t(`event.gender.${value}`),
        value: value,
    })), [t, i18n.language]);
    const shirtSizeOptions = useMemo(() => Object.values(ShirtSize).map((value) => ({
        label: value,
        value: value,
    })), [t, i18n.language]);
    const selectedOption = useWatch({
        control,
        name: "PersonalInfo.Option",
        defaultValue: undefined,
    });
    const willYouAttendEventWatch = useWatch({
        control,
        name: "PersonalInfo.WillYouAttendEvent",
        defaultValue: undefined,
    });
    useEffect(() => {
        // When option changes, handle field clearing and validation
        if (selectedOption === 1) {
            // Coming alone - clear adults and children counts
            setValue("PersonalInfo.AdultsCount", 0, { shouldValidate: true });
            setValue("PersonalInfo.ChildrenCount", 0, { shouldValidate: true });
            clearErrors("PersonalInfo.AdultsCount");
            clearErrors("PersonalInfo.ChildrenCount");
        }
        else if (selectedOption === 2) {
            // Coming with introduction - set adults to 1, clear children
            setValue("PersonalInfo.AdultsCount", 1, { shouldValidate: true });
            setValue("PersonalInfo.ChildrenCount", 0, { shouldValidate: true });
            clearErrors("PersonalInfo.AdultsCount");
            clearErrors("PersonalInfo.ChildrenCount");
        }
        else if (selectedOption === 3) {
            // Family option - keep existing values but ensure validation
            const currentAdults = personalInfo?.adultsCount
                ? personalInfo?.adultsCount
                : control._formValues?.PersonalInfo?.AdultsCount || 1;
            const currentChildren = personalInfo?.childrenCount
                ? personalInfo?.childrenCount
                : control._formValues?.PersonalInfo?.ChildrenCount || 0;
            setValue("PersonalInfo.AdultsCount", currentAdults, {
                shouldValidate: true,
            });
            setValue("PersonalInfo.ChildrenCount", currentChildren, {
                shouldValidate: true,
            });
        }
    }, [selectedOption, setValue, clearErrors]);
    useEffect(() => {
        if (formMod === "view") {
            const currentUserId = externalId;
            const currentInvitationId = invitationId;
            dispatch(getInvitationDatesByInvitationIdAsync(currentInvitationId));
        }
        else {
            if (linkVerification?.data?.invitationId) {
                dispatch(getInvitationDatesByInvitationIdAsync(linkVerification?.data?.invitationId));
            }
        }
    }, [dispatch, linkVerification?.data?.invitationId, formMod]);
    useEffect(() => {
        if (externalId) {
            dispatch(GetExternalByUserIdAsync(externalId))
                .unwrap()
                .then((response) => {
                const obj = {
                    UserId: response.userId,
                    InvitationId: response.invitationId,
                };
                dispatch(GetPersonalInfoByUserIdAsync(obj))
                    .unwrap()
                    .then((response) => { });
            });
        }
        else if (invitationId && userId) {
            const obj = {
                UserId: userId,
                InvitationId: invitationId,
            };
            dispatch(GetPersonalInfoByUserIdAsync(obj));
        }
        else if (linkVerification?.data?.userId) {
            const obj = {
                UserId: linkVerification?.data?.userId,
                InvitationId: linkVerification?.data?.invitationId,
            };
            dispatch(GetPersonalInfoByUserIdAsync(obj));
        }
    }, [linkVerification?.data?.userId, externalId, invitationId, userId]);
    const handleFlagChange = (isNL) => {
        if (isNL === isNLAddress) {
            return;
        }
        setIsNLAddress(isNL);
        setValue("PersonalInfo.language", isNL ? "NL" : "INT", {
            shouldValidate: true,
        });
        if (!isNL) {
            setValue("PersonalInfo.postalcode", "", { shouldValidate: false });
            setValue("PersonalInfo.houseNumber", "", { shouldValidate: false });
            setValue("PersonalInfo.addition", "", { shouldValidate: false });
            clearErrors("PersonalInfo.Address");
            setValue("PersonalInfo.Address", "", { shouldValidate: false });
        }
        else {
            clearErrors("PersonalInfo.Address");
            setValue("PersonalInfo.Address", "", { shouldValidate: false });
        }
    };
    const handleAdultsChange = (updatedAdults) => { };
    useEffect(() => {
        persistor.purge();
    }, [persistor]);
    return eventType === "family" ? (_jsxs(_Fragment, { children: [_jsx(SteperHeader, { title: t("event.personalInformation") }), _jsxs(Grid, { container: true, spacing: 1, mt: 3, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, defaultValue: "", label: t("formFields.firstName"), name: "PersonalInfo.FirstName", error: errors.PersonalInfo?.FirstName?.message, rules: {
                                required: {
                                    value: true,
                                    message: "validations.firstNameRequired",
                                },
                            }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, label: t("formFields.middleName"), name: "PersonalInfo.MiddleName", disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, label: t("formFields.lastName"), name: "PersonalInfo.LastName", error: errors.PersonalInfo?.LastName?.message, rules: {
                                required: {
                                    value: true,
                                    message: "validations.lastNameRequired",
                                },
                            }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, label: t("formFields.email"), name: "PersonalInfo.email", disabled: true, defaultValue: linkVerification?.data?.user.email }) }), _jsx(Grid, { size: { xs: 3, sm: 3, md: 2 }, children: _jsx(FlagDropdown, { onChange: handleFlagChange, defaultValue: personalInfo?.language || "NL", disabled: isRegistered }) }), isNLAddress && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: { xs: 9, md: 3 }, children: _jsx(HInput, { control: control, name: "PersonalInfo.postalcode", label: t("formFields.postalCode"), rules: {
                                        required: {
                                            value: true,
                                            message: "validations.postalCodeRequired",
                                        },
                                        pattern: {
                                            value: /^[a-zA-Z0-9\s]+$/,
                                            message: "validations.invalidPostalCode",
                                        },
                                    }, error: errors.PersonalInfo?.postalcode?.message, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(HInput, { control: control, name: "PersonalInfo.houseNumber", label: t("formFields.houseNo"), rules: {
                                        required: {
                                            value: true,
                                            message: "validations.houseNumberRequired",
                                        },
                                    }, error: errors.PersonalInfo?.houseNumber?.message, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(HInput, { control: control, name: "PersonalInfo.addition", label: t("formFields.addition"), rules: {
                                        required: {
                                            value: showAddition,
                                            message: "validations.additionRequired",
                                        },
                                    }, error: errors.PersonalInfo?.addition?.message, disabled: isRegistered }) })] })), _jsx(Grid, { size: {
                            xs: isNLAddress ? 12 : 9,
                            sm: isNLAddress ? 12 : 9,
                            md: isNLAddress ? 12 : 10,
                        }, children: _jsx(HInput, { control: control, name: "PersonalInfo.Address", label: t("formFields.address"), 
                            // backgroundColor={isNLAddress ? "#00244D1A" : ""}
                            disabled: isNLAddress || isRegistered, rules: {
                                validate: {
                                    // Check for manual errors first (like address not found)
                                    manualError: (value) => {
                                        if (errors.PersonalInfo?.Address?.type === "manual") {
                                            return errors.PersonalInfo.Address.message || true;
                                        }
                                        return true;
                                    },
                                    // Then check for required validation
                                    required: (value) => {
                                        if (!value || value.trim().length === 0) {
                                            return "validations.addressRequired";
                                        }
                                        return true;
                                    },
                                },
                            }, error: errors.PersonalInfo?.Address?.message }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 12 }, children: _jsx(PhoneInputMasking, { control: control, label: t("formFields.mobileNumber"), name: "PersonalInfo.Phone", error: errors.PersonalInfo?.Phone?.message, rules: {
                                required: {
                                    value: eventType === "externals" ? false : true,
                                    message: "validations.mobileRequired",
                                },
                            }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 12 }, children: _jsx(HDatePicker, { label: t("formFields.dateOfBirth"), control: control, name: "PersonalInfo.DateOfBirth", rules: {
                                required: {
                                    value: true,
                                    message: "validations.dateOfBirthRequired",
                                },
                                validate: {
                                    isValidDate: (value) => {
                                        return validateDate(value);
                                    },
                                    notFutureDate: (value) => {
                                        return validateIsFutureDate(value);
                                    },
                                },
                            }, errormsg: errors?.PersonalInfo?.DateOfBirth?.message, disabled: isRegistered, disableFuture: true }) }), _jsx(Box, { sx: {
                            border: "1px solid #00244D33",
                            background: "#00244D0A",
                            px: 2.5,
                            py: 2,
                            borderRadius: 1,
                            mt: 1.5,
                            width: "100%",
                        }, children: _jsxs(Grid, { container: true, spacing: 1, children: [_jsxs(Grid, { size: 12, children: [_jsx(Typography, { color: "#00244D", sx: {
                                                fontFamily: "SuisseIntl",
                                                fontWeight: 500,
                                                fontSize: 16,
                                            }, children: selectedOption === 1
                                                ? t("event.personalInfoTextAlone")
                                                : t("event.personalInfoText") }), _jsx(Typography, { sx: {
                                                fontFamily: "SuisseIntl",
                                                fontSize: { xs: 11, sm: 12, md: 16 },
                                                fontWeight: 400,
                                            }, color: "#00244D99", mb: 1, mt: 1, children: t("event.personalInfoSubText") }), _jsx(HTabInput, { control: control, error: errors.PersonalInfo?.Option?.message, options: [
                                                { label: t("formFields.family"), value: 3 },
                                                { label: t("formFields.introduction"), value: 2 },
                                                { label: t("formFields.alone"), value: 1 },
                                            ], name: "PersonalInfo.Option", rules: {
                                                required: {
                                                    value: true,
                                                    message: "validations.chooseOption",
                                                },
                                            }, label: t("formFields.comingWith"), disabled: isRegistered })] }), selectedOption === 2 && eventType === "family" && (_jsx(Grid, { size: 12, children: _jsx(AdultsInfoForm, { control: control, errors: errors, adultsCount: 1, introduction: true, personId: personId, formMod: formMod, onAdultsChange: handleAdultsChange, selectedOption: selectedOption, setValue: setValue, isRelationRequired: false, reset: reset }) })), selectedOption === 3 && eventType === "family" && (_jsxs(Grid, { size: 12, children: [_jsx(Typography, { variant: "subtitle1", fontWeight: 500, color: "#00244D", children: t("formFields.howManyAdults") }), _jsxs(Box, { sx: { ...colCenter, alignItems: "start", gap: 1 }, children: [_jsx(HInput, { label: "", hiddenLabel: true, type: "number", placeholder: t("placeholder.quantity"), control: control, name: "PersonalInfo.AdultsCount", error: errors.PersonalInfo?.AdultsCount?.message, rules: {
                                                        required: {
                                                            value: true,
                                                            message: "validations.numberOfAdultsRequired",
                                                        },
                                                        // min: {
                                                        //   value: 0,
                                                        //   message: "validations.numberOfAdultsMin",
                                                        // },
                                                        min: {
                                                            value: (adultsCount ?? 0) >= 1 ? 0 : 1,
                                                            message: (adultsCount ?? 0) >= 1
                                                                ? "validations.numberOfAdultsMin"
                                                                : "validations.atLeastOneRequired",
                                                        },
                                                    }, disabled: isRegistered }), _jsx(EIconButton, { fixedHeight: true, disabled: isRegistered, height: "53px", onClick: () => setValue("PersonalInfo.AdultsCount", Math.max(adultsCount - 1, 0), {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                        shouldTouch: true,
                                                    }), children: _jsx(Minus, {}) }), _jsx(EIconButton, { fixedHeight: true, height: "53px", disabled: isRegistered, onClick: () => setValue("PersonalInfo.AdultsCount", adultsCount + 1, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                        shouldTouch: true,
                                                    }), children: _jsx(Add, {}) })] })] })), selectedOption === 3 && eventType === "family" && (_jsxs(Grid, { size: 12, children: [_jsx(Typography, { variant: "subtitle1", fontWeight: 500, color: "#00244D", children: t("formFields.howManyChildren") }), _jsxs(Box, { sx: { ...colCenter, alignItems: "start", gap: 1 }, children: [_jsx(HInput
                                                // label={t("formFields.howManyChildren")}
                                                , { 
                                                    // label={t("formFields.howManyChildren")}
                                                    type: "number", hiddenLabel: true, placeholder: t("placeholder.quantity"), control: control, name: "PersonalInfo.ChildrenCount", error: errors.PersonalInfo?.ChildrenCount?.message, rules: {
                                                        required: {
                                                            value: true,
                                                            message: "validations.numberOfChildrenRequired",
                                                        },
                                                        min: {
                                                            value: 0,
                                                            message: "validations.numberOfChildrenMin",
                                                        },
                                                    }, disabled: isRegistered }), _jsx(EIconButton, { fixedHeight: true, height: "53px", disabled: isRegistered, onClick: () => setValue("PersonalInfo.ChildrenCount", Math.max(childrenCount - 1, 0), {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                        shouldTouch: true,
                                                    }), children: _jsx(Minus, {}) }), _jsx(EIconButton, { fixedHeight: true, disabled: isRegistered, height: "53px", onClick: () => setValue("PersonalInfo.ChildrenCount", childrenCount + 1, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                        shouldTouch: true,
                                                    }), children: _jsx(Add, {}) })] })] }))] }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(SteperHeader, { title: t("event.directorPersonalInformation") }), _jsxs(Grid, { container: true, spacing: 1, mt: 3, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, 
                            // defaultValue={
                            //   linkVerification?.data?.user.externalCompanyName ||
                            //   personalInfo?.companyName?.includes(
                            //     "Van Dijnsen Beveiligingstechniek"
                            //   )
                            //     ? "Van Dijnsen"
                            //     : personalInfo?.companyName
                            // }
                            defaultValue: linkVerification?.data?.user.externalCompanyName ||
                                personalInfo?.companyName?.includes("Van Dijnsen Beveiligingstechniek")
                                ? "Van Dijnsen"
                                : personalInfo?.companyName, label: t("formFields.selectCompany"), name: "PersonalInfo.CompanyName", 
                            //error={errors.PersonalInfo?.FirstName?.message}
                            disabled: true }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, defaultValue: "", label: t("formFields.firstName"), name: "PersonalInfo.FirstName", error: errors.PersonalInfo?.FirstName?.message, rules: {
                                required: {
                                    value: true,
                                    message: "validations.firstNameRequired",
                                },
                            }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, label: t("formFields.middleName"), name: "PersonalInfo.MiddleName", disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, label: t("formFields.lastName"), name: "PersonalInfo.LastName", error: errors.PersonalInfo?.LastName?.message, rules: {
                                required: {
                                    value: true,
                                    message: "validations.lastNameRequired",
                                },
                            }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HInput, { control: control, label: t("formFields.email"), name: "PersonalInfo.email", disabled: true, defaultValue: linkVerification?.data?.user.email }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { sx: {
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: 1,
                                border: (theme) => `1px solid ${alpha(theme.palette.edark.main, 0.2)}`,
                                borderColor: (theme) => errors.PersonalInfo?.WillYouAttendEvent?.message
                                    ? theme.palette.error.main
                                    : alpha(theme.palette.edark.main, 0.2),
                                backgroundColor: (theme) => alpha(theme.palette.edark.main, 0.04),
                                py: 2,
                                px: 2.5,
                                gap: 0.5,
                            }, children: _jsxs(Grid, { container: true, spacing: 1, children: [_jsx(Grid, { size: 12, children: _jsx(HTabInput, { control: control, error: errors.PersonalInfo?.WillYouAttendEvent?.message, options: [
                                                { label: t("actions.yes"), value: "true" },
                                                { label: t("actions.no"), value: "false" },
                                            ], name: "PersonalInfo.WillYouAttendEvent", label: t("formFields.areYouComing"), disabled: formMod === "view", rules: {
                                                required: {
                                                    value: true,
                                                    message: "validations.chooseOption",
                                                },
                                            } }) }), willYouAttendEventWatch !== undefined &&
                                        willYouAttendEventWatch === "true" && (_jsx(Grid, { size: 12, children: _jsx(HSwitchGroup, { label: eventType === "externals"
                                                ? t("formFields.externalAttendaceDates")
                                                : t("formFields.attendanceDates"), options: attendanceOptions, name: "PersonalInfo.AttendanceDateIds", control: control, error: errors.PersonalInfo?.AttendanceDateIds?.message, rules: {
                                                required: {
                                                    value: true,
                                                    message: "validations.chooseDateOption",
                                                },
                                            }, disabled: formMod === "view" }) }))] }) }) }), willYouAttendEventWatch !== undefined &&
                        willYouAttendEventWatch === "true" && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: { xs: 12 }, children: _jsx(Typography, { variant: "subtitle2", sx: {
                                        color: "#00244D",
                                        flexGrow: 1,
                                    }, children: t("formFields.enterAddressHere") }) }), _jsx(Grid, { size: { xs: 3, sm: 3, md: 2 }, children: _jsx(FlagDropdown, { onChange: handleFlagChange, defaultValue: personalInfo?.language || "NL", disabled: isRegistered }) }), isNLAddress && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: { xs: 9, md: 3 }, children: _jsx(HInput, { control: control, name: "PersonalInfo.postalcode", label: t("formFields.postalCode"), rules: {
                                                required: {
                                                    value: true,
                                                    message: "validations.postalCodeRequired",
                                                },
                                                pattern: {
                                                    value: /^[a-zA-Z0-9\s]+$/,
                                                    message: "validations.invalidPostalCode",
                                                },
                                            }, error: errors.PersonalInfo?.postalcode?.message, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(HInput, { control: control, name: "PersonalInfo.houseNumber", label: t("formFields.houseNo"), rules: {
                                                required: {
                                                    value: true,
                                                    message: "validations.houseNumberRequired",
                                                },
                                            }, error: errors.PersonalInfo?.houseNumber?.message, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(HInput, { control: control, name: "PersonalInfo.addition", label: t("formFields.addition"), rules: {
                                                required: {
                                                    value: showAddition,
                                                    message: "validations.additionRequired",
                                                },
                                            }, error: errors.PersonalInfo?.addition?.message, disabled: isRegistered }) })] })), _jsx(Grid, { size: {
                                    xs: isNLAddress ? 12 : 9,
                                    sm: isNLAddress ? 12 : 9,
                                    md: isNLAddress ? 12 : 10,
                                }, children: _jsx(HInput, { control: control, name: "PersonalInfo.Address", label: t("formFields.address"), 
                                    // backgroundColor={isNLAddress ? "#00244D1A" : ""}
                                    disabled: isNLAddress || isRegistered, rules: {
                                        validate: {
                                            // Check for manual errors first (like address not found)
                                            manualError: (value) => {
                                                if (errors.PersonalInfo?.Address?.type === "manual") {
                                                    return errors.PersonalInfo.Address.message || true;
                                                }
                                                return true;
                                            },
                                            // Then check for required validation
                                            required: (value) => {
                                                if (!value || value.trim().length === 0) {
                                                    return "validations.addressRequired";
                                                }
                                                return true;
                                            },
                                        },
                                    }, error: errors.PersonalInfo?.Address?.message }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(PhoneInputMasking, { control: control, label: t("formFields.mobileNumber"), name: "PersonalInfo.Phone", error: errors.PersonalInfo?.Phone?.message, rules: {
                                        required: {
                                            value: eventType === "externals" ? false : true,
                                            message: "validations.mobileRequired",
                                        },
                                        validate: (value) => {
                                            // Accepts only numbers, must be at least 8 digits
                                            const phoneRegex = /^\d{8,}$/;
                                            if (!phoneRegex.test(value || "")) {
                                                return "validations.phoneNoIsInvalid";
                                            }
                                            return true;
                                        },
                                    }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, lg: 6 }, children: _jsx(HSelect, { control: control, label: t("formFields.gender"), name: "PersonalInfo.Gender", options: genderOptions, error: errors.PersonalInfo?.Gender?.message, rules: {
                                        required: {
                                            value: true,
                                            message: "validations.genderRequired",
                                        },
                                    }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, lg: 6 }, children: _jsx(HSelect, { control: control, label: t("formFields.shirtSize"), name: "PersonalInfo.ShirtSize", options: shirtSizeOptions, error: errors.PersonalInfo?.ShirtSize?.message, rules: {
                                        required: {
                                            value: true,
                                            message: "validations.shirtSizeRequired",
                                        },
                                    }, disabled: isRegistered }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 12 }, children: _jsx(HDatePicker, { label: t("formFields.dateOfBirth"), control: control, name: "PersonalInfo.DateOfBirth", rules: {
                                        required: {
                                            value: true,
                                            message: "validations.dateOfBirthRequired",
                                        },
                                        validate: {
                                            isValidDate: (value) => {
                                                return validateDate(value);
                                            },
                                            notFutureDate: (value) => {
                                                return validateIsFutureDate(value);
                                            },
                                        },
                                    }, errormsg: errors?.PersonalInfo?.DateOfBirth?.message, disabled: isRegistered, disableFuture: true }) })] }))] })] }));
};
